import { useEffect, useRef, useState } from "react";
import { Check, PackageCheck } from "lucide-react";
import { createPortal } from "react-dom";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { useProductPage } from "@/features/product/hooks";
import { useSuppliers } from "@/features/supplier/hooks";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { CloseIconButton } from "@/components/ui/CloseIconButton";
import {
  formatQuantityDisplay,
  getQuantityInputMode,
  getQuantityInputStep,
  parsePositiveQuantityInput,
} from "@/lib/quantity";
import { formatBRL } from "@/lib/timeAgo";
import { SearchCombobox, type SearchComboboxOption } from "./SearchCombobox";
import {
  formatReplacementResult,
  getAllowedComposerMode,
  isReplacementNoop,
  type CancellationReceivedHandling,
  type ComposerMode,
  type OriginalHandling,
} from "./replacementComposerState";

const unitLabels: Record<string, string> = {
  kg: "Kg",
  cx_kg: "Caixa (kg)",
  cx_un: "Caixa (un)",
  bdj: "Bandeja",
  un: "Unidade",
  sc_kg: "Saco (kg)",
};

export type ReplacementComposerOriginalItem = {
  productId: string;
  productName: string;
  supplierId: string;
  supplierName: string;
  unitType: string;
  requestedQuantity: number;
  receivedQuantity: number;
  pendingQuantity: number;
  realPrice: number;
  finalPrice: number;
};

export type ReplacementComposerSubmission = {
  original_handling: OriginalHandling;
  additional_original_quantity?: number;
  replacement: {
    product_id: string;
    supplier_id: string;
    unit_type: string;
    quantity: number;
    real_price: number;
    final_price: number;
  };
};

export type CancellationComposerSubmission = {
  cancellation_received_handling: CancellationReceivedHandling;
  note?: string;
};

type ReplacementComposerProps = {
  appearance?: "notification";
  open: boolean;
  original: ReplacementComposerOriginalItem;
  allowReplacement?: boolean;
  allowCancellation?: boolean;
  initialMode?: ComposerMode;
  pending?: boolean;
  onClose: () => void;
  onReplace: (submission: ReplacementComposerSubmission) => Promise<void>;
  onCancelItem?: (submission: CancellationComposerSubmission) => Promise<void>;
  onSuccess?: (mode: ComposerMode, result: string) => void;
  getErrorMessage?: (error: unknown) => string;
};

export function ReplacementComposer({
  appearance,
  open,
  original,
  allowReplacement = true,
  allowCancellation = false,
  initialMode = "replacement",
  pending = false,
  onClose,
  onReplace,
  onCancelItem,
  onSuccess,
  getErrorMessage = (error) =>
    getUserFacingErrorMessage(
      error,
      "Não foi possível concluir a operação. Tente novamente.",
    ),
}: ReplacementComposerProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState<ComposerMode>(
    allowReplacement ? initialMode : "cancellation",
  );
  const [handling, setHandling] = useState<OriginalHandling>("keep_received");
  const [cancellationHandling, setCancellationHandling] =
    useState<CancellationReceivedHandling>("keep_received");
  const [additional, setAdditional] = useState("");
  const [productId, setProductId] = useState(original.productId);
  const [productQuery, setProductQuery] = useState(original.productName);
  const [supplierId, setSupplierId] = useState(original.supplierId);
  const [supplierQuery, setSupplierQuery] = useState(original.supplierName);
  const [unitType, setUnitType] = useState(original.unitType);
  const [quantity, setQuantity] = useState(String(original.requestedQuantity));
  const [realPrice, setRealPrice] = useState(String(original.realPrice));
  const [finalPrice, setFinalPrice] = useState(String(original.finalPrice));
  const [note, setNote] = useState("");
  const [openCombobox, setOpenCombobox] = useState<
    "product" | "supplier" | null
  >(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const catalogEnabled = open && mode === "replacement" && allowReplacement;
  const debouncedProductQuery = useDebouncedValue(productQuery.trim(), 250);
  const debouncedSupplierQuery = useDebouncedValue(supplierQuery.trim(), 250);
  const products = useProductPage(
    {
      limit: 50,
      offset: 0,
      search: debouncedProductQuery,
    },
    catalogEnabled,
  );
  const suppliers = useSuppliers({
    limit: 50,
    offset: 0,
    search: debouncedSupplierQuery,
    enabled: catalogEnabled,
  });

  useDialogFocus(dialogRef, onClose, { enabled: open });

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = window.requestAnimationFrame(() => setVisible(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setVisible(false);
    const timeout = window.setTimeout(() => setMounted(false), 180);
    return () => window.clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setMode(
      allowReplacement && initialMode === "replacement"
        ? "replacement"
        : "cancellation",
    );
    setHandling("keep_received");
    setCancellationHandling("keep_received");
    setAdditional("");
    setProductId(original.productId);
    setProductQuery(original.productName);
    setSupplierId(original.supplierId);
    setSupplierQuery(original.supplierName);
    setUnitType(original.unitType);
    setQuantity(String(original.requestedQuantity));
    setRealPrice(String(original.realPrice));
    setFinalPrice(String(original.finalPrice));
    setNote("");
    setOpenCombobox(null);
    setError(null);
  }, [open]);

  useEffect(() => {
    setMode((current) =>
      getAllowedComposerMode(current, allowReplacement, allowCancellation),
    );
  }, [allowCancellation, allowReplacement, mode]);

  const productSearchPending =
    productQuery.trim() !== debouncedProductQuery || products.isFetching;
  const supplierSearchPending =
    supplierQuery.trim() !== debouncedSupplierQuery || suppliers.isFetching;
  const productOptions = productSearchPending
    ? []
    : (products.data?.items ?? []).map((product) => ({
        id: product.id,
        label: product.name,
        description: unitLabels[product.default_unit] ?? product.default_unit,
      }));
  const supplierOptions = supplierSearchPending
    ? []
    : (suppliers.data?.items ?? []).map((supplier) => ({
        id: supplier.id,
        label: supplier.name,
      }));
  const catalogLoading = productSearchPending || supplierSearchPending;
  const catalogError = products.isError || suppliers.isError;
  const parsedQuantity = parsePositiveQuantityInput(quantity, unitType);
  const parsedAdditional = parsePositiveQuantityInput(
    additional,
    original.unitType,
  );
  const parsedRealPrice = realPrice.trim() ? Number(realPrice) : Number.NaN;
  const parsedFinalPrice = finalPrice.trim() ? Number(finalPrice) : Number.NaN;
  const replacementSnapshot = {
    productId,
    supplierId,
    unitType,
    quantity: parsedQuantity ?? Number.NaN,
    realPrice: parsedRealPrice,
    finalPrice: parsedFinalPrice,
  };
  const noOp = isReplacementNoop({
    original: {
      productId: original.productId,
      supplierId: original.supplierId,
      unitType: original.unitType,
      quantity: original.requestedQuantity,
      realPrice: original.realPrice,
      finalPrice: original.finalPrice,
    },
    replacement: replacementSnapshot,
    handling,
  });
  const replacementResult = formatReplacementResult({
    handling,
    received: original.receivedQuantity,
    additional: parsedAdditional ?? 0,
    replacement: parsedQuantity ?? 0,
  });
  const cancellationResult =
    original.receivedQuantity <= 0
      ? "Item cancelado sem quantidade recebida."
      : cancellationHandling === "return_received"
        ? `${formatQuantityDisplay(original.receivedQuantity, original.unitType)} recebidas serão devolvidas e o item será cancelado.`
        : `${formatQuantityDisplay(original.receivedQuantity, original.unitType)} recebidas serão mantidas e o item será cancelado.`;
  const isBusy = pending || submitting;
  const showTabs = allowReplacement && allowCancellation;

  function selectMode(nextMode: ComposerMode) {
    setMode(nextMode);
    if (nextMode === "cancellation") setOpenCombobox(null);
    setError(null);
  }

  function selectProduct(option: SearchComboboxOption) {
    setProductId(option.id);
    setProductQuery(option.label);
    const product = products.data?.items.find(
      (entry) => entry.id === option.id,
    );
    if (product) setUnitType(product.default_unit);
    setError(null);
  }

  function selectSupplier(option: SearchComboboxOption) {
    setSupplierId(option.id);
    setSupplierQuery(option.label);
    setError(null);
  }

  async function submitReplacement() {
    if (catalogError) {
      setError("Não foi possível carregar o catálogo. Tente novamente.");
      return;
    }
    if (!productId || !supplierId) {
      setError("Escolha produto e fornecedor cadastrados.");
      return;
    }
    if (
      parsedQuantity == null ||
      !Number.isFinite(parsedRealPrice) ||
      parsedRealPrice < 0 ||
      !Number.isFinite(parsedFinalPrice) ||
      parsedFinalPrice < 0
    ) {
      setError("Informe quantidade e preços válidos.");
      return;
    }
    if (handling === "keep_and_receive_more" && parsedAdditional == null) {
      setError("Informe uma quantidade adicional maior que zero.");
      return;
    }
    if (noOp) {
      setError("Altere ao menos um campo ou o destino do item original.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onReplace({
        original_handling: handling,
        ...(handling === "keep_and_receive_more"
          ? { additional_original_quantity: parsedAdditional! }
          : {}),
        replacement: {
          product_id: productId,
          supplier_id: supplierId,
          unit_type: unitType,
          quantity: parsedQuantity,
          real_price: parsedRealPrice,
          final_price: parsedFinalPrice,
        },
      });
      onSuccess?.("replacement", replacementResult);
      onClose();
    } catch (cause) {
      setError(getErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitCancellation() {
    if (!onCancelItem) return;
    setSubmitting(true);
    setError(null);
    try {
      await onCancelItem({
        cancellation_received_handling: cancellationHandling,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onSuccess?.("cancellation", cancellationResult);
      onClose();
    } catch (cause) {
      setError(getErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  }

  if (!mounted) return null;

  return createPortal(
    <div
      className="order-replacement-backdrop"
      data-state={visible ? "open" : "closing"}
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget && !isBusy) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="order-replacement-modal"
        data-appearance={appearance}
        data-state={visible ? "open" : "closing"}
        data-has-tabs={showTabs}
        role="dialog"
        aria-modal="true"
        aria-labelledby="replacement-composer-title"
        tabIndex={-1}
      >
        <header className="order-replacement-header">
          <div>
            <p className="order-replacement-kicker">Decisão do item</p>
            <h2 id="replacement-composer-title">
              {mode === "replacement" ? "Substituir item" : "Cancelar item"}
            </h2>
          </div>
          <CloseIconButton
            className="order-replacement-close"
            onClick={onClose}
            disabled={isBusy}
            label="Fechar compositor"
            iconSize={20}
          />
        </header>

        {showTabs ? (
          <div
            className="order-replacement-tabs"
            role="tablist"
            aria-label="Ação do item"
            onKeyDown={(event) => {
              const tabs = Array.from(
                event.currentTarget.querySelectorAll<HTMLButtonElement>(
                  '[role="tab"]',
                ),
              );
              const current = tabs.indexOf(
                document.activeElement as HTMLButtonElement,
              );
              let next = current;
              if (event.key === "ArrowRight")
                next = (current + 1) % tabs.length;
              else if (event.key === "ArrowLeft")
                next = (current - 1 + tabs.length) % tabs.length;
              else if (event.key === "Home") next = 0;
              else if (event.key === "End") next = tabs.length - 1;
              else return;
              event.preventDefault();
              const nextMode = next === 0 ? "replacement" : "cancellation";
              selectMode(nextMode);
              tabs[next]?.focus();
            }}
          >
            <button
              type="button"
              id="replacement-tab"
              role="tab"
              aria-selected={mode === "replacement"}
              aria-controls="replacement-panel"
              tabIndex={mode === "replacement" ? 0 : -1}
              onClick={() => selectMode("replacement")}
            >
              Substituir
            </button>
            <button
              type="button"
              id="cancellation-tab"
              role="tab"
              aria-selected={mode === "cancellation"}
              aria-controls="cancellation-panel"
              tabIndex={mode === "cancellation" ? 0 : -1}
              onClick={() => selectMode("cancellation")}
            >
              Cancelar item
            </button>
          </div>
        ) : null}

        <div className="order-replacement-scroll">
          <section
            className="order-replacement-summary"
            aria-labelledby="original-item-title"
          >
            <div className="order-replacement-summary-heading">
              <PackageCheck size={19} aria-hidden="true" />
              <div>
                <p id="original-item-title">Item original</p>
                <strong>{original.productName}</strong>
                <span className="order-replacement-summary-meta">
                  {unitLabels[original.unitType] ?? original.unitType} ·{" "}
                  {original.supplierName}
                </span>
              </div>
            </div>
            <dl>
              <div>
                <dt>Solicitada</dt>
                <dd>
                  {formatQuantityDisplay(
                    original.requestedQuantity,
                    original.unitType,
                  )}
                </dd>
              </div>
              <div>
                <dt>Recebida</dt>
                <dd>
                  {formatQuantityDisplay(
                    original.receivedQuantity,
                    original.unitType,
                  )}
                </dd>
              </div>
              <div>
                <dt>Pendente</dt>
                <dd>
                  {formatQuantityDisplay(
                    original.pendingQuantity,
                    original.unitType,
                  )}
                </dd>
              </div>
              <div>
                <dt>Custo / cliente</dt>
                <dd>
                  {formatBRL(original.realPrice)} /{" "}
                  {formatBRL(original.finalPrice)}
                </dd>
              </div>
            </dl>
          </section>

          <div
            id={`${mode}-panel`}
            className="order-replacement-panel"
            data-mode={mode}
            role={showTabs ? "tabpanel" : undefined}
            aria-labelledby={
              showTabs
                ? mode === "replacement"
                  ? "replacement-tab"
                  : "cancellation-tab"
                : undefined
            }
          >
            {mode === "replacement" ? (
              <div className="order-replacement-columns">
                <fieldset className="order-replacement-handling">
                  <legend>Destino do item original</legend>
                  {(
                    [
                      [
                        "keep_received",
                        "Manter o que recebemos",
                        "Preserva recebimento e histórico.",
                      ],
                      [
                        "keep_and_receive_more",
                        "Manter e receber mais",
                        "Adiciona uma quantidade do fornecedor original.",
                      ],
                      [
                        "return_all",
                        "Devolver tudo",
                        "Registra devolução do total recebido.",
                      ],
                    ] as const
                  ).map(([value, title, description]) => (
                    <label
                      key={value}
                      data-selected={handling === value || undefined}
                    >
                      <input
                        className="order-replacement-handling-input"
                        type="radio"
                        name="original-handling"
                        value={value}
                        checked={handling === value}
                        disabled={isBusy}
                        onChange={() => {
                          setHandling(value);
                          setError(null);
                        }}
                      />
                      <span>
                        <strong>{title}</strong>
                        <small>{description}</small>
                      </span>
                      <Check
                        className="order-replacement-handling-check"
                        size={16}
                        aria-hidden="true"
                      />
                    </label>
                  ))}
                  {handling === "keep_and_receive_more" ? (
                    <label className="replacement-field replacement-additional-field">
                      <span>Quantidade adicional</span>
                      <input
                        aria-label="Quantidade adicional"
                        type="number"
                        min="0"
                        step={getQuantityInputStep(original.unitType)}
                        inputMode={getQuantityInputMode(original.unitType)}
                        value={additional}
                        disabled={isBusy}
                        onChange={(event) => {
                          setAdditional(event.target.value);
                          setError(null);
                        }}
                      />
                    </label>
                  ) : null}
                </fieldset>

                <section
                  className="order-replacement-offer"
                  aria-labelledby="new-offer-title"
                >
                  <h3 id="new-offer-title">Nova oferta</h3>
                  <div className="replacement-catalog-fields">
                    <SearchCombobox
                      label="Produto"
                      placeholder="Buscar produto"
                      query={productQuery}
                      value={productId}
                      options={productOptions}
                      open={openCombobox === "product"}
                      loading={productSearchPending}
                      disabled={isBusy || catalogError}
                      onQueryChange={(nextQuery) => {
                        setProductQuery(nextQuery);
                        setProductId("");
                        setSupplierQuery("");
                        setSupplierId("");
                        setOpenCombobox("product");
                      }}
                      onOpenChange={(nextOpen) =>
                        setOpenCombobox(nextOpen ? "product" : null)
                      }
                      onSelect={selectProduct}
                    />
                    <SearchCombobox
                      label="Fornecedor"
                      placeholder="Buscar fornecedor"
                      query={supplierQuery}
                      value={supplierId}
                      options={supplierOptions}
                      open={openCombobox === "supplier"}
                      loading={supplierSearchPending}
                      disabled={isBusy || catalogError}
                      onQueryChange={(nextQuery) => {
                        setSupplierQuery(nextQuery);
                        setSupplierId("");
                        setOpenCombobox("supplier");
                      }}
                      onOpenChange={(nextOpen) =>
                        setOpenCombobox(nextOpen ? "supplier" : null)
                      }
                      onSelect={selectSupplier}
                    />
                  </div>
                  {catalogError ? (
                    <p className="order-replacement-error" role="alert">
                      Não foi possível carregar o catálogo.{" "}
                      <button
                        type="button"
                        onClick={() => {
                          void products.refetch();
                          void suppliers.refetch();
                        }}
                      >
                        Tentar novamente
                      </button>
                    </p>
                  ) : null}
                  <div className="order-replacement-fields">
                    <label className="replacement-field">
                      <span>Unidade</span>
                      <span className="replacement-select-wrap">
                        <select
                          aria-label="Unidade"
                          value={unitType}
                          disabled={isBusy}
                          onChange={(event) => setUnitType(event.target.value)}
                        >
                          {Object.entries(unitLabels).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </span>
                    </label>
                    <label className="replacement-field">
                      <span>Quantidade</span>
                      <input
                        aria-label="Quantidade substituta"
                        type="number"
                        min="0"
                        step={getQuantityInputStep(unitType)}
                        inputMode={getQuantityInputMode(unitType)}
                        value={quantity}
                        disabled={isBusy}
                        onChange={(event) => setQuantity(event.target.value)}
                      />
                    </label>
                    <label className="replacement-field">
                      <span>Custo do fornecedor</span>
                      <input
                        aria-label="Custo do fornecedor"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={realPrice}
                        disabled={isBusy}
                        onChange={(event) => setRealPrice(event.target.value)}
                      />
                    </label>
                    <label className="replacement-field">
                      <span>Preço do cliente</span>
                      <input
                        aria-label="Preço do cliente"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={finalPrice}
                        disabled={isBusy}
                        onChange={(event) => setFinalPrice(event.target.value)}
                      />
                    </label>
                  </div>
                </section>
              </div>
            ) : (
              <section
                className="order-cancellation-panel"
                aria-labelledby="cancellation-title"
              >
                <div>
                  <h3 id="cancellation-title">
                    O que acontece com o que já chegou?
                  </h3>
                  <p>A decisão fica registrada no histórico operacional.</p>
                </div>
                <fieldset className="order-replacement-handling order-cancellation-options">
                  <legend className="sr-only">
                    Destino das quantidades recebidas
                  </legend>
                  <label
                    data-selected={
                      cancellationHandling === "keep_received" || undefined
                    }
                  >
                    <input
                      className="order-replacement-handling-input"
                      type="radio"
                      name="cancellation-handling"
                      checked={cancellationHandling === "keep_received"}
                      disabled={isBusy}
                      onChange={() => setCancellationHandling("keep_received")}
                    />
                    <span>
                      <strong>Manter recebidas</strong>
                      <small>Conserva estoque e histórico recebidos.</small>
                    </span>
                    <Check
                      className="order-replacement-handling-check"
                      size={16}
                      aria-hidden="true"
                    />
                  </label>
                  <label
                    data-selected={
                      cancellationHandling === "return_received" || undefined
                    }
                  >
                    <input
                      className="order-replacement-handling-input"
                      type="radio"
                      name="cancellation-handling"
                      checked={cancellationHandling === "return_received"}
                      disabled={isBusy}
                      onChange={() =>
                        setCancellationHandling("return_received")
                      }
                    />
                    <span>
                      <strong>Devolver recebidas</strong>
                      <small>Registra devolução exata no movimento.</small>
                    </span>
                    <Check
                      className="order-replacement-handling-check"
                      size={16}
                      aria-hidden="true"
                    />
                  </label>
                </fieldset>
                <label className="replacement-field order-cancellation-note">
                  <span>Nota operacional (Opcional)</span>
                  <textarea
                    rows={3}
                    maxLength={500}
                    value={note}
                    disabled={isBusy}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Contexto para a equipe"
                  />
                </label>
              </section>
            )}
          </div>
        </div>

        <footer className="order-replacement-actions">
          <div className="order-replacement-result" aria-live="polite">
            <Check size={16} aria-hidden="true" />
            <span>
              {mode === "replacement" ? replacementResult : cancellationResult}
            </span>
          </div>
          {error || (mode === "replacement" && noOp) ? (
            <p className="order-replacement-error" role="alert">
              {error ??
                "Altere ao menos um campo ou o destino do item original."}
            </p>
          ) : null}
          <div className="order-replacement-action-buttons">
            <button type="button" onClick={onClose} disabled={isBusy}>
              Voltar
            </button>
            <button
              type="button"
              onClick={() =>
                void (mode === "replacement"
                  ? submitReplacement()
                  : submitCancellation())
              }
              disabled={
                isBusy ||
                (mode === "replacement" &&
                  (noOp || catalogLoading || catalogError))
              }
            >
              {isBusy
                ? "Salvando..."
                : mode === "replacement"
                  ? "Confirmar substituição"
                  : "Confirmar cancelamento"}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
